import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { ChevronRight, ChevronDown, FileDown } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./inventoryItemFlowTable.css";

function iso(d) {
  return d.toISOString().slice(0, 10);
}
function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfYear(d = new Date()) {
  return new Date(d.getFullYear(), 0, 1);
}
function clampISO(s) {
  return s ? String(s).slice(0, 10) : "";
}
function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function pct(n) {
  const v = Number(n || 0);
  return `${(v * 100).toFixed(1)}%`;
}

// --- Fetch movement lines (warehouse on inventory_movements - you confirmed B) ---
async function fetchLines({ companyId, from, to, warehouseId }) {
  // We try with optional sku/unit_cost; fallback if columns are missing.
  const selectFull = `
    id,
    item_id,
    sku,
    quantity,
    unit_cost,
    inventory_movements!inner(
      id,
      company_id,
      warehouse_id,
      movement_type,
      movement_date
    )
  `;
  const selectNoSku = `
    id,
    item_id,
    quantity,
    unit_cost,
    inventory_movements!inner(
      id,
      company_id,
      warehouse_id,
      movement_type,
      movement_date
    )
  `;
  const selectMinimal = `
    id,
    item_id,
    quantity,
    inventory_movements!inner(
      id,
      company_id,
      warehouse_id,
      movement_type,
      movement_date
    )
  `;

  const run = async (selectStr) => {
    let q = supabase
      .from("inventory_movement_lines")
      .select(selectStr)
      .eq("inventory_movements.company_id", companyId)
      .gte("inventory_movements.movement_date", from)
      .lte("inventory_movements.movement_date", to);

    if (warehouseId) q = q.eq("inventory_movements.warehouse_id", warehouseId);
    return await q;
  };

  let res = await run(selectFull);
  if (!res.error)
    return { data: res.data || [], hasSku: true, hasUnitCost: true };

  res = await run(selectNoSku);
  if (!res.error)
    return { data: res.data || [], hasSku: false, hasUnitCost: true };

  res = await run(selectMinimal);
  if (res.error) throw res.error;

  return { data: res.data || [], hasSku: false, hasUnitCost: false };
}

async function fetchWarehouses(companyId) {
  const { data, error } = await supabase
    .from("warehouses")
    .select("id,name")
    .eq("company_id", companyId)
    .order("name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export default function InventoryItemFlowTable({
  companyId,
  items = [],
  balances = [],
  defaultPreset = "last_week",
}) {
  const todayISO = iso(new Date());

  const [preset, setPreset] = useState(defaultPreset);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(todayISO);

  const [showSales, setShowSales] = useState(true);
  const [showPurchases, setShowPurchases] = useState(true);

  const [itemSearch, setItemSearch] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");

  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(""); // "" = all warehouses

  // Collapsed-by-default groups
  const [expandedItems, setExpandedItems] = useState(() => new Set());

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // We build grouped rows: { itemId, name, dailyRows[], subtotalRow }
  const [groups, setGroups] = useState([]);

  // Maps
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const itemBySku = useMemo(() => {
    const m = new Map();
    items.forEach((i) => {
      if (i?.sku) m.set(String(i.sku).toLowerCase(), i);
    });
    return m;
  }, [items]);

  // Current qty per item (respects warehouse filter via balances.warehouse_id)
  const currentQtyByItem = useMemo(() => {
    const m = new Map();
    (balances || []).forEach((b) => {
      if (warehouseId && b.warehouse_id !== warehouseId) return;
      m.set(
        b.item_id,
        Number(m.get(b.item_id) || 0) + Number(b.qty_on_hand || 0)
      );
    });
    return m;
  }, [balances, warehouseId]);

  // Threshold per item (buffer/reorder)
  const thresholdByItem = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      const threshold = Math.max(
        Number(it.buffer_stock || 0),
        Number(it.reorder_level || 0)
      );
      m.set(it.id, threshold);
    });
    return m;
  }, [items]);

  // Avg cost by item from balances (warehouse-aware)
  const avgCostByItem = useMemo(() => {
    const sumQty = new Map();
    const sumCost = new Map();

    (balances || []).forEach((b) => {
      if (warehouseId && b.warehouse_id !== warehouseId) return;

      const q = Number(b.qty_on_hand || 0);
      const c = Number(b.avg_cost || 0);
      sumQty.set(b.item_id, (sumQty.get(b.item_id) || 0) + q);
      sumCost.set(b.item_id, (sumCost.get(b.item_id) || 0) + q * c);
    });

    const out = new Map();
    for (const [itemId, totalQty] of sumQty.entries()) {
      const totalCost = Number(sumCost.get(itemId) || 0);
      out.set(itemId, totalQty > 0 ? totalCost / totalQty : 0);
    }
    return out;
  }, [balances, warehouseId]);

  // presets
  useEffect(() => {
    const now = new Date();
    let from = "";
    let to = todayISO;

    if (preset === "last_week") {
      const d0 = new Date(now);
      d0.setDate(d0.getDate() - 7);
      from = iso(d0);
    } else if (preset === "last_month") {
      const d0 = new Date(now);
      d0.setDate(d0.getDate() - 30);
      from = iso(d0);
    } else if (preset === "last_year") {
      const d0 = new Date(now);
      d0.setFullYear(d0.getFullYear() - 1);
      from = iso(d0);
    } else if (preset === "this_month") {
      from = iso(startOfMonth(now));
    } else if (preset === "ytd") {
      from = iso(startOfYear(now));
    } else if (preset === "custom") {
      return;
    }

    setFromDate(from);
    setToDate(to);
  }, [preset, todayISO]);

  // warehouses
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const ws = await fetchWarehouses(companyId);
        setWarehouses(ws);
      } catch {
        setWarehouses([]);
      }
    })();
  }, [companyId]);

  const toIsToday = clampISO(toDate) === todayISO;

  function toggleItem(itemId) {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  function buildExcel() {
    // Export what user sees: subtotals always + expanded daily rows
    const rows = [];
    groups.forEach((g) => {
      const isOpen = expandedItems.has(g.itemId);
      if (isOpen) {
        g.dailyRows.forEach((r) => rows.push(r));
      }
      rows.push(g.subtotalRow);
    });

    const exportRows = rows.map((r) => ({
      Item: r.name,
      Date: r.dateLabel,
      "Sold Qty": r.sold_qty,
      "Bought Qty": r.bought_qty,
      "Sales Price": r.sales_price,
      "Avg Cost": r.avg_cost,
      "Sales Amount": r.sold_amount,
      "Purchase Amount": r.bought_amount,
      "COGS (Est.)": r.cogs,
      "Gross Profit": r.gross_profit,
      "Margin %": r.margin_pct,
      "Running Balance": r.running_balance ?? "",
      "Opening (period)": r.opening_qty ?? "",
      "Closing (today)": r.closing_qty ?? "",
      "Low Stock?": r.low_stock ? "YES" : "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory Flow");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const wh = warehouseId
      ? warehouses.find((w) => w.id === warehouseId)?.name || "Warehouse"
      : "All-Warehouses";

    saveAs(blob, `inventory_flow_${wh}_${fromDate}_to_${toDate}.xlsx`);
  }

  async function load() {
    if (!companyId) return;

    const f = clampISO(fromDate);
    const t = clampISO(toDate);

    if (!f || !t) {
      setGroups([]);
      return;
    }
    if (!showSales && !showPurchases) {
      setGroups([]);
      return;
    }

    setLoading(true);
    setErr("");

    try {
      const { data, hasSku, hasUnitCost } = await fetchLines({
        companyId,
        from: f,
        to: t,
        warehouseId: warehouseId || null,
      });

      // Aggregate per (item, date)
      const byItemDate = new Map(); // key: `${itemId}|${date}` -> row

      const resolveItem = (ln) => {
        const rawItemId = ln.item_id || "";
        const lineSku = hasSku && ln.sku ? String(ln.sku).toLowerCase() : "";

        const byId = rawItemId ? itemById.get(rawItemId) : null;
        const bySku = !byId && lineSku ? itemBySku.get(lineSku) : null;

        const resolved = byId || bySku;
        const resolvedId =
          resolved?.id || rawItemId || `unknown:${lineSku || ln.id}`;

        return {
          resolvedId,
          item: resolved,
          lineSku,
        };
      };

      for (const ln of data || []) {
        const mv = ln.inventory_movements;
        if (!mv) continue;

        const type = mv.movement_type;
        if (type === "IN" && !showPurchases) continue;
        if (type === "OUT" && !showSales) continue;
        if (type !== "IN" && type !== "OUT") continue;

        const { resolvedId, item } = resolveItem(ln);

        // item filters
        if (selectedItemId && resolvedId !== selectedItemId) continue;

        const name = item?.name || "Unknown";
        if (itemSearch.trim()) {
          const q = itemSearch.trim().toLowerCase();
          if (!name.toLowerCase().includes(q)) continue;
        }

        const date = mv.movement_date;
        const qty = Number(ln.quantity || 0);

        const salesPrice = Number(item?.sales_price || 0);
        const avgCostFallback = Number(avgCostByItem.get(resolvedId) || 0);

        const unitCost =
          hasUnitCost && ln.unit_cost != null
            ? Number(ln.unit_cost || 0)
            : avgCostFallback;

        const key = `${resolvedId}|${date}`;
        const cur = byItemDate.get(key) || {
          kind: "day",
          itemId: resolvedId,
          name,
          date,
          dateLabel: date,
          sold_qty: 0,
          bought_qty: 0,
          sales_price: salesPrice,
          avg_cost: avgCostFallback,
          sold_amount: 0,
          bought_amount: 0,
          cogs: 0,
          gross_profit: 0,
          margin_pct: "",
          running_balance: null,
          opening_qty: null,
          closing_qty: null,
          low_stock: false,
        };

        // keep latest sales price if non-zero
        if (salesPrice) cur.sales_price = salesPrice;

        if (type === "OUT") {
          cur.sold_qty += qty;
          cur.sold_amount += qty * cur.sales_price;
        } else {
          cur.bought_qty += qty;
          cur.bought_amount += qty * unitCost;
        }

        // we keep avg_cost as fallback avg cost (warehouse-aware)
        cur.avg_cost = avgCostFallback;

        byItemDate.set(key, cur);
      }

      // Group by item -> sort by date -> compute running balance and subtotals
      const byItem = new Map();
      for (const r of byItemDate.values()) {
        const arr = byItem.get(r.itemId) || [];
        arr.push(r);
        byItem.set(r.itemId, arr);
      }

      const nextGroups = [];

      for (const [itemId, dayRows] of byItem.entries()) {
        const it = itemById.get(itemId);
        const name = it?.name || dayRows[0]?.name || "Unknown";
        const threshold = Number(thresholdByItem.get(itemId) || 0);

        // sort by date ASC
        dayRows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

        // net change in period (for opening calc if toDate is today)
        const periodSold = dayRows.reduce(
          (s, r) => s + Number(r.sold_qty || 0),
          0
        );
        const periodBought = dayRows.reduce(
          (s, r) => s + Number(r.bought_qty || 0),
          0
        );
        const periodNet = periodBought - periodSold;

        const closingQty = Number(currentQtyByItem.get(itemId) || 0);
        const openingQty = toIsToday ? closingQty - periodNet : null;

        // Running balance:
        // - if toDate is today -> start from openingQty
        // - otherwise -> show running delta from 0 (still useful), but not absolute
        let running = toIsToday ? Number(openingQty || 0) : 0;

        // compute cogs/gp/margin + running
        for (const r of dayRows) {
          const cogs = Number(r.sold_qty || 0) * Number(r.avg_cost || 0);
          const gp = Number(r.sold_amount || 0) - cogs;
          const margin =
            Number(r.sold_amount || 0) > 0
              ? gp / Number(r.sold_amount || 0)
              : 0;

          r.cogs = cogs;
          r.gross_profit = gp;
          r.margin_pct = Number(r.sold_amount || 0) > 0 ? pct(margin) : "";

          running =
            running + Number(r.bought_qty || 0) - Number(r.sold_qty || 0);
          r.running_balance = running;
        }

        // subtotal row
        const soldAmount = dayRows.reduce(
          (s, r) => s + Number(r.sold_amount || 0),
          0
        );
        const boughtAmount = dayRows.reduce(
          (s, r) => s + Number(r.bought_amount || 0),
          0
        );
        const cogsSum = dayRows.reduce((s, r) => s + Number(r.cogs || 0), 0);
        const gpSum = dayRows.reduce(
          (s, r) => s + Number(r.gross_profit || 0),
          0
        );
        const margin = soldAmount > 0 ? gpSum / soldAmount : 0;

        const lowStock = threshold > 0 && closingQty <= threshold;

        const subtotalRow = {
          kind: "subtotal",
          itemId,
          name,
          date: "Subtotal",
          dateLabel: "Subtotal",
          sold_qty: periodSold,
          bought_qty: periodBought,
          sales_price: Number(it?.sales_price || 0),
          avg_cost: Number(avgCostByItem.get(itemId) || 0),
          sold_amount: soldAmount,
          bought_amount: boughtAmount,
          cogs: cogsSum,
          gross_profit: gpSum,
          margin_pct: soldAmount > 0 ? pct(margin) : "",
          running_balance: toIsToday ? closingQty : running, // last running value
          opening_qty: openingQty,
          closing_qty: closingQty,
          low_stock: lowStock,
        };

        nextGroups.push({
          itemId,
          name,
          dailyRows: dayRows,
          subtotalRow,
        });
      }

      // Sort groups by name
      nextGroups.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      setGroups(nextGroups);

      // UI polish: collapse by default (only subtotals)
      // (We do NOT auto-expand items even if they match; user can expand.)
      // Keep user expansions as-is.
    } catch (e) {
      setErr(e?.message || "Failed to load item flow.");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    fromDate,
    toDate,
    showSales,
    showPurchases,
    selectedItemId,
    itemSearch,
    warehouseId,
    items,
    balances,
  ]);

  const grandTotals = useMemo(() => {
    const subs = groups.map((g) => g.subtotalRow);
    const soldQty = subs.reduce((s, r) => s + Number(r.sold_qty || 0), 0);
    const boughtQty = subs.reduce((s, r) => s + Number(r.bought_qty || 0), 0);
    const soldAmt = subs.reduce((s, r) => s + Number(r.sold_amount || 0), 0);
    const boughtAmt = subs.reduce(
      (s, r) => s + Number(r.bought_amount || 0),
      0
    );
    const cogs = subs.reduce((s, r) => s + Number(r.cogs || 0), 0);
    const gp = subs.reduce((s, r) => s + Number(r.gross_profit || 0), 0);
    const margin = soldAmt > 0 ? gp / soldAmt : 0;
    return { soldQty, boughtQty, soldAmt, boughtAmt, cogs, gp, margin };
  }, [groups]);

  const modeLabel = useMemo(() => {
    if (showSales && showPurchases) return "Sales + Purchases";
    if (showSales) return "Sales (Items Sold)";
    return "Purchases (Items Bought)";
  }, [showSales, showPurchases]);

  return (
    <div className="inv-flow-card">
      <div className="inv-flow-head">
        <div>
          <div className="inv-flow-title">Items Sold / Purchased</div>
          <div className="inv-flow-sub">
            Per day with subtotals — <b>{modeLabel}</b>
            {!toIsToday && (
              <span className="inv-flow-warn">
                {" "}
                • Running balance is relative (period delta) unless “To” is
                today.
              </span>
            )}
          </div>
        </div>

        <div className="inv-flow-controls">
          <button
            className="inv-flow-btn inv-flow-btn-icon"
            onClick={buildExcel}
            disabled={loading || groups.length === 0}
          >
            <FileDown size={16} /> Export Excel
          </button>

          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <option value="">All warehouses</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>

          <select value={preset} onChange={(e) => setPreset(e.target.value)}>
            <option value="last_week">Last week</option>
            <option value="last_month">Last month</option>
            <option value="this_month">This month</option>
            <option value="ytd">Year to date</option>
            <option value="last_year">Last year</option>
            <option value="custom">Custom</option>
          </select>

          <div className="inv-flow-dates">
            <label>
              From
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setPreset("custom");
                  setFromDate(e.target.value);
                }}
              />
            </label>

            <label>
              To
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setPreset("custom");
                  setToDate(e.target.value);
                }}
              />
            </label>
          </div>

          <div className="inv-flow-toggles">
            <label className="chk">
              <input
                type="checkbox"
                checked={showSales}
                onChange={(e) => setShowSales(e.target.checked)}
              />
              Sales
            </label>
            <label className="chk">
              <input
                type="checkbox"
                checked={showPurchases}
                onChange={(e) => setShowPurchases(e.target.checked)}
              />
              Purchases
            </label>
          </div>
        </div>
      </div>

      <div className="inv-flow-filterrow">
        <input
          className="inv-flow-search"
          placeholder="Search item name e.g. parfait"
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
        />

        <select
          className="inv-flow-itemselect"
          value={selectedItemId}
          onChange={(e) => setSelectedItemId(e.target.value)}
        >
          <option value="">All items</option>
          {items
            .slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map((it) => (
              <option key={it.id} value={it.id}>
                {it.name}
              </option>
            ))}
        </select>

        <button className="inv-flow-btn" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Apply"}
        </button>
      </div>

      <div className="inv-flow-body">
        {err && <div className="inv-flow-error">{err}</div>}

        <table className="inv-flow-table">
          <thead>
            <tr>
              <th style={{ minWidth: 280 }}>Item</th>
              <th style={{ width: 120 }}>Date</th>

              <th style={{ textAlign: "right" }}>Sold</th>
              <th style={{ textAlign: "right" }}>Bought</th>

              <th style={{ textAlign: "right" }}>Sales Price</th>
              <th style={{ textAlign: "right" }}>Avg Cost</th>

              <th style={{ textAlign: "right" }}>Sales Amt</th>
              <th style={{ textAlign: "right" }}>Purchase Amt</th>

              <th style={{ textAlign: "right" }}>COGS</th>
              <th style={{ textAlign: "right" }}>Gross Profit</th>
              <th style={{ textAlign: "right" }}>Margin</th>

              <th style={{ textAlign: "right" }}>Running Bal</th>
              <th style={{ textAlign: "right" }}>Closing</th>
            </tr>
          </thead>

          <tbody>
            {groups.map((g) => {
              const open = expandedItems.has(g.itemId);
              const sub = g.subtotalRow;

              return (
                <React.Fragment key={g.itemId}>
                  {/* Subtotal (always visible, acts like a header row) */}
                  <tr className="inv-flow-subrow">
                    <td className="inv-flow-item">
                      <button
                        className="inv-flow-expand"
                        onClick={() => toggleItem(g.itemId)}
                        title={open ? "Collapse" : "Expand"}
                      >
                        {open ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>

                      <div className="inv-flow-itemtext">
                        <div className="name">
                          {g.name}
                          {sub.low_stock && (
                            <span className="inv-badge inv-badge-warn">
                              Low stock
                            </span>
                          )}
                        </div>
                        <div className="meta">
                          <span>Subtotal</span>
                          {toIsToday && sub.opening_qty != null && (
                            <span>
                              • Opening:{" "}
                              <b>
                                {Number(sub.opening_qty || 0).toLocaleString()}
                              </b>
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td style={{ fontWeight: 950 }}>Subtotal</td>

                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {Number(sub.sold_qty || 0).toLocaleString()}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {Number(sub.bought_qty || 0).toLocaleString()}
                    </td>

                    <td style={{ textAlign: "right" }}>
                      {money(sub.sales_price)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {money(sub.avg_cost)}
                    </td>

                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {money(sub.sold_amount)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {money(sub.bought_amount)}
                    </td>

                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {money(sub.cogs)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {money(sub.gross_profit)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {sub.margin_pct}
                    </td>

                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {sub.running_balance != null
                        ? Number(sub.running_balance || 0).toLocaleString()
                        : ""}
                    </td>

                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {sub.closing_qty != null
                        ? Number(sub.closing_qty || 0).toLocaleString()
                        : ""}
                    </td>
                  </tr>

                  {/* Daily rows (collapsed by default) */}
                  {open &&
                    g.dailyRows.map((r) => (
                      <tr
                        key={`${g.itemId}-${r.date}`}
                        className="inv-flow-dayrow"
                      >
                        <td />
                        <td>{r.dateLabel}</td>

                        <td style={{ textAlign: "right" }}>
                          {Number(r.sold_qty || 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {Number(r.bought_qty || 0).toLocaleString()}
                        </td>

                        <td style={{ textAlign: "right" }}>
                          {money(r.sales_price)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {money(r.avg_cost)}
                        </td>

                        <td style={{ textAlign: "right" }}>
                          {money(r.sold_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {money(r.bought_amount)}
                        </td>

                        <td style={{ textAlign: "right" }}>{money(r.cogs)}</td>
                        <td style={{ textAlign: "right" }}>
                          {money(r.gross_profit)}
                        </td>
                        <td style={{ textAlign: "right" }}>{r.margin_pct}</td>

                        <td style={{ textAlign: "right" }}>
                          {r.running_balance != null
                            ? Number(r.running_balance || 0).toLocaleString()
                            : ""}
                        </td>

                        <td />
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}

            {!loading && groups.length === 0 && (
              <tr>
                <td colSpan={13} className="inv-flow-empty">
                  No matching movements for the selected filters.
                </td>
              </tr>
            )}

            {loading && (
              <tr>
                <td colSpan={13} className="inv-flow-empty">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan={2} style={{ fontWeight: 950 }}>
                Grand Totals
              </td>

              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {Number(grandTotals.soldQty || 0).toLocaleString()}
              </td>
              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {Number(grandTotals.boughtQty || 0).toLocaleString()}
              </td>

              <td colSpan={2} />

              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {money(grandTotals.soldAmt)}
              </td>
              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {money(grandTotals.boughtAmt)}
              </td>

              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {money(grandTotals.cogs)}
              </td>
              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {money(grandTotals.gp)}
              </td>
              <td style={{ textAlign: "right", fontWeight: 950 }}>
                {grandTotals.soldAmt > 0 ? pct(grandTotals.margin) : ""}
              </td>

              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
